import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { insertFatura, atualizarGuiaConvenio } from '../services/prontuario';
import { toBrazilMidnightIso } from '../util/utils';
import atividadeParaFatura from '../util/atividadeParaFatura';
import resultadoInsertFaturamento from '../util/resultadoInsertFaturamento';
import useDetalhes from './useDetalhes';
import useProcedimentos from './useProcedimentos';

const SMALLMONEY_MAX = 214748.3647;

const isValidSmallmoney = v => {
  const n = Number(v);
  return Number.isFinite(n) && Math.abs(n) <= SMALLMONEY_MAX;
};

const CODMED_CONVENIADO_BLOQUEADO = 1066;
const CONVENIOS_PARTICULAR_BLOQUEADOS = [25, 802];
const MSG_CONVENIO_NAO_AUTORIZA_1066 =
  'Convênio não autoriza faturar para esse médico 1066';
const MSG_FATURAMENTO_LIBERADO =
  'Faturamento já liberado! Não pode ser alterado.';

const isFaturamentoConveniadoBloqueado = fatura =>
  Number(fatura.codConveniado) === CODMED_CONVENIADO_BLOQUEADO &&
  CONVENIOS_PARTICULAR_BLOQUEADOS.includes(Number(fatura.codConv));

const isFaturamentoLiberado = fatura => fatura?.liberado === 'SIM';

const toDateInputValue = value => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getDetalheValor = (detalhes, codProcedi, codConv) =>
  detalhes.find(d => d.CODPROCEDI === codProcedi && d.CODCONV === codConv)
    ?.VLRREAL || 0;

const getProcedimentoNome = (procedimentos, codProcedi) =>
  procedimentos.find(p => p.CODPROCEDI === codProcedi)?.DESCPROCEDI;

const useFaturamento = () => {
  const medico = useSelector(state => state.doctor.profile);
  const { funcionario } = useSelector(state => state.funcionario);
  const { procedimentos } = useProcedimentos();
  const { detalhes } = useDetalhes();

  const [fatura, setFatura] = useState({
    codRealizou: '',
    codProcedi: '',
    codConv: '',
    codPac: '',
    dataFatura: '',
    convenio: '',
    procedimento: '',
    guia: '',
    valor: 0,
    guiaecg: '',
    ecg: '',
    eco: '',
    // Quantidade de Fetos dinâmic para ECO-FETAL (CODPROCEDI 17)
    qtdFetos: 1,
  });

  const faturaRef = useRef(fatura);
  const [showModalFaturamento, setShowModalFaturamento] = useState(false);
  const [showModalUpdateFat, setShowModalUpdateFat] = useState(false);
  const [loadingFaturamento, setLoadingFaturamento] = useState(false);

  useEffect(() => {
    faturaRef.current = fatura;
  }, [fatura]);

  const handleChangeFaturamento = useCallback(e => {
    e.persist();
    const { name, value, type, checked } = e.target;
    let val = type === 'checkbox' ? checked : value;

    // Garantir que qtdFetos seja sempre um número válido >= 1
    if (name === 'qtdFetos') {
      val = Math.max(1, Number(value) || 1);
    }

    setFatura(prev => ({
      ...prev,
      [name]: val,
    }));
  }, []);

  // Altera a quantidade de fetos dinamicamente por atalho/botão
  const setQuantidadeFetos = useCallback(qtd => {
    const parsedQtd = Math.max(1, Number(qtd) || 1);
    setFatura(prev => ({
      ...prev,
      qtdFetos: parsedQtd,
    }));
  }, []);

  const handleValorFaturamento = useCallback(
    (e, opts = {}) => {
      const codProcedi = Number(e.target.value);
      const codConv =
        opts.codConv != null
          ? Number(opts.codConv)
          : Number(faturaRef.current.codConv);

      const procedimento = procedimentos.find(
        p => Number(p.CODPROCEDI) === codProcedi
      );

      if (!procedimento) {
        toast.warn('Procedimento não localizado!');
        return;
      }

      const detalhe = detalhes.find(
        d => d.CODPROCEDI === codProcedi && d.CODCONV === codConv
      );

      const valor = detalhe?.VLRREAL || 0;

      setFatura(prevFatura => ({
        ...prevFatura,
        codProcedi,
        procedimento: procedimento.DESCPROCEDI,
        valor,
        valorecg: codProcedi === 3 ? valor : 0,
        // Mantém a quantidade de fetos atual se for Eco Fetal (17), senão reseta para 1
        qtdFetos: codProcedi === 17 ? prevFatura.qtdFetos || 1 : 1,
      }));
    },
    [procedimentos, detalhes]
  );

  const optarPorEcg = useCallback(
    ecgId => {
      const conv = Number(faturaRef.current.codConv);
      if (!conv) {
        toast.warn('Informe o convênio antes de escolher o tipo de ECG.');
        return;
      }

      const vlr1 = getDetalheValor(detalhes, 1, conv);
      const vlr3 = getDetalheValor(detalhes, 3, conv);
      const nome1 = getProcedimentoNome(procedimentos, 1);
      const nome3 = getProcedimentoNome(procedimentos, 3);

      setFatura(prev => {
        if (ecgId === '31') {
          return {
            ...prev,
            codProcedi: 3,
            ecg: '31',
            procedimento: nome3 || prev.procedimento,
            valor: vlr1,
            valorecg: vlr3,
          };
        }
        if (ecgId === '3') {
          return {
            ...prev,
            codProcedi: 3,
            ecg: '3',
            procedimento: nome3 || prev.procedimento,
            valor: vlr3,
            valorecg: vlr3,
          };
        }
        if (ecgId === '1') {
          return {
            ...prev,
            codProcedi: 1,
            ecg: '1',
            procedimento: nome1 || prev.procedimento,
            valor: vlr1,
            valorecg: 0,
          };
        }
        return prev;
      });
    },
    [detalhes, procedimentos]
  );

  const handleAtualizarGuiaConvenio = useCallback(
    pAtividade => {
      const conv = Number(pAtividade.CODCONV);
      const detalheEcg =
        pAtividade.CODPROCEDI === 3
          ? detalhes.find(d => d.CODPROCEDI === 3 && d.CODCONV === conv)
          : null;
      const vlrLaudo = Number(pAtividade.VLRLAUDO);
      let valorecg = 0;

      if (pAtividade.CODPROCEDI === 3) {
        if (
          detalheEcg &&
          detalheEcg.VLRREAL != null &&
          detalheEcg.VLRREAL !== ''
        ) {
          valorecg = Number(detalheEcg.VLRREAL) || 0;
        } else if (isValidSmallmoney(vlrLaudo)) {
          valorecg = vlrLaudo;
        }
      }

      setFatura({
        ...atividadeParaFatura(pAtividade),
        usuario: medico ? medico.apelido : funcionario?.nome,
        dataFormatada: toDateInputValue(pAtividade.DATALAUDO),
        dataFatura: toDateInputValue(pAtividade.DATALAUDO),
        valorecg,
        liberado: pAtividade.GUIA1,
      });
      setShowModalUpdateFat(true);
    },
    [medico, funcionario, detalhes]
  );

  const handleUpdateFaturamento = useCallback(
    async event => {
      event.preventDefault();

      if (isFaturamentoLiberado(fatura)) {
        toast.warn(MSG_FATURAMENTO_LIBERADO);
        return false;
      }

      if (isFaturamentoConveniadoBloqueado(fatura)) {
        toast.error(MSG_CONVENIO_NAO_AUTORIZA_1066);
        return false;
      }

      if (!fatura.dataFatura) {
        toast.warn('Informe a data do faturamento.');
        return false;
      }

      try {
        const btnUpdateFat = document.getElementById('btnUpdateFat');
        if (btnUpdateFat) btnUpdateFat.disabled = true;
        const result = await atualizarGuiaConvenio({
          ...fatura,
          dataFatura: toBrazilMidnightIso(fatura.dataFatura),
        });
        toast.success(result);
        return true;
      } catch (error) {
        toast.error(
          error?.response?.data?.error || 'Erro ao atualizar faturamento!'
        );
        return false;
      } finally {
        const btnUpdateFat = document.getElementById('btnUpdateFat');
        if (btnUpdateFat) btnUpdateFat.disabled = false;
      }
    },
    [fatura]
  );

  const handleExcluirFaturamento = useCallback(
    async event => {
      if (isFaturamentoLiberado(fatura)) {
        return toast.warn(MSG_FATURAMENTO_LIBERADO);
      }

      try {
        event.preventDefault();
        const response = await api.delete(`/faturamento`, { data: fatura });
        toast.success(response.data.message);
      } catch (error) {
        toast.error(
          error?.response?.data?.error || 'Erro ao excluir faturamento!'
        );
      }
    },
    [fatura]
  );

  const handleInsertFaturamento = useCallback(
    async event => {
      event.preventDefault();

      if (isFaturamentoConveniadoBloqueado(fatura)) {
        toast.error(MSG_CONVENIO_NAO_AUTORIZA_1066);
        return;
      }

      const codProcedi = Number(fatura.codProcedi);
      if (codProcedi === 3 && !isValidSmallmoney(fatura.valorecg)) {
        toast.warn('Valor ECG inválido ou fora do limite!');
        return;
      }

      if (!isValidSmallmoney(fatura.valor)) {
        toast.warn('Valor de faturamento inválido ou fora do limite!');
        return;
      }

      try {
        const data = await insertFatura({
          ...fatura,
          dataFatura: toBrazilMidnightIso(fatura.dataFatura),
        });

        const resultado = resultadoInsertFaturamento({
          data,
          codProcedi,
          codPac: fatura.codPac,
        });

        if (resultado.aviso) {
          toast.warn(resultado.aviso);
          return;
        }

        toast.success(resultado.message || 'Faturamento Gravado com Sucesso!');
        return {
          message: resultado.message,
          showAlertEspelho: resultado.showAlertEspelho,
          faturado: resultado.faturado,
        };
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Erro ao faturar!');
      }
    },
    [fatura]
  );

  return {
    fatura,
    setFatura,
    showModalFaturamento,
    setShowModalFaturamento,
    showModalUpdateFat,
    setShowModalUpdateFat,
    loadingFaturamento,
    setLoadingFaturamento,
    handleChangeFaturamento,
    handleValorFaturamento,
    setQuantidadeFetos,
    optarPorEcg,
    handleAtualizarGuiaConvenio,
    handleUpdateFaturamento,
    handleExcluirFaturamento,
    handleInsertFaturamento,
  };
};

export default useFaturamento;